/**
 * This browser's memory of the chat, one key per `name` and retailer. Never
 * throws: in private mode it just forgets on reload.
 */
export function readStored(name: string, slug: string): string | null {
	try {
		return localStorage.getItem(`hephon-commerce:${name}:${slug}`);
	} catch {
		return null;
	}
}

/** `null` forgets the value. */
export function writeStored(name: string, slug: string, value: string | null) {
	try {
		const key = `hephon-commerce:${name}:${slug}`;
		if (value === null) {
			localStorage.removeItem(key);
		} else {
			localStorage.setItem(key, value);
		}
	} catch {
		// Private mode: it lasts until reload.
	}
}

/** The tab's shopper session and the conversation it is on (ADR-5.3). */
export interface StoredSession {
	token: string | null;
	conversationId: string | null;
}

/**
 * Per tab (sessionStorage), per retailer and store: the token is a bearer secret, and a
 * branch switch is another basket.
 */
const sessionKey = (slug: string, store: string | null) =>
	`hephon-commerce:session:${slug}:${store ?? ""}`;

export function readSession(slug: string, store: string | null): StoredSession {
	try {
		const raw = sessionStorage.getItem(sessionKey(slug, store));
		const value = raw ? (JSON.parse(raw) as Partial<StoredSession>) : {};
		return {
			token: value.token ?? null,
			conversationId: value.conversationId ?? null,
		};
	} catch {
		return { token: null, conversationId: null };
	}
}

export function writeSession(
	slug: string,
	store: string | null,
	value: StoredSession,
) {
	try {
		sessionStorage.setItem(sessionKey(slug, store), JSON.stringify(value));
	} catch {
		// Private mode: it lasts until reload.
	}
}
